import { BulkMigrationFactory } from '@domains/subscription/priv/factories/BulkMigrationFactory'
import { Templates } from '@domains/subscription/priv/Templates/Templates'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  useCreateDefaultConfig,
  useCreateTemplate,
  useCreateTemplateFromExisting,
  useCreateTemplateOptions,
  useGetDefaultConfigs,
  useGetSupportedCurrencies,
  useGetSupportedPartners,
  useGetTemplates,
} from '../api/queries'
import type { CreateDefaultConfigFormData, CreateTemplateFormData } from '../types'

type TemplatesAdapterProps = {
  navigateToTemplate: (templateId: string) => void
  navigateToDefaultConfig: (configId: string) => void
}

export const TemplatesFactory = ({ navigateToTemplate, navigateToDefaultConfig }: TemplatesAdapterProps) => {
  const { data: templates = [] } = useGetTemplates()
  const { data: options, isLoading: isLoadingOptions } = useCreateTemplateOptions()
  const { data: defaultConfigs = [] } = useGetDefaultConfigs()
  const { data: supportedCurrencies = [] } = useGetSupportedCurrencies()
  const { data: supportedPartners = [] } = useGetSupportedPartners()
  const createTemplate = useCreateTemplate()
  const createTemplateFromExisting = useCreateTemplateFromExisting()
  const createDefaultConfig = useCreateDefaultConfig()

  const navigate = useNavigate()
  const location = useLocation()
  const search = useSearchParams()

  const isCreatingTemplate = createTemplate.isLoading || createTemplateFromExisting.isLoading

  const notify = useMaterialNotification()

  const onCreateTemplate = async ({ navigateToTemplate: shouldNavigate, copyFromExisting, ...templateData }: CreateTemplateFormData) => {
    const onSuccess = (data: { id: string }) => {
      notify('Template created successfully')

      if (shouldNavigate) {
        navigateToTemplate(data.id)
      }
    }

    if (copyFromExisting) {
      await createTemplateFromExisting.mutateAsync(
        {
          templateId: templateData.templateToCopy,
          eligibility: templateData.eligibility,
          externalName: templateData.externalName,
          name: templateData.name,
        },
        { onSuccess },
      )
    } else {
      await createTemplate.mutateAsync(templateData, { onSuccess })
    }
  }

  const onSaveDefaultConfig = (configData: CreateDefaultConfigFormData): void => {
    createDefaultConfig.mutate(configData, {
      onSuccess: () => {
        notify('Default config created successfully')
      },
    })
  }

  if (isLoadingOptions || !options) {
    return null
  }

  return (
    <Templates
      isPatching={isCreatingTemplate}
      templates={templates}
      options={options}
      defaultConfigs={defaultConfigs}
      onNavigateToTemplate={navigateToTemplate}
      onNavigateToDefaultConfig={navigateToDefaultConfig}
      onCreateTemplate={onCreateTemplate}
      onSaveDefaultConfig={onSaveDefaultConfig}
      supportedCurrencies={supportedCurrencies}
      supportedPartners={supportedPartners}
      bulkMigrationSlot={<BulkMigrationFactory />}
      pathname={location.pathname}
      onNavigate={navigate}
      search={search}
    />
  )
}
